import React, { useState } from 'react';

import { Chip, ChipList, ChipProps } from '@/components/shared/Chip';
import { Label } from '@/components/shared/Inputs/Label';
import { SingleActionLayout } from '@/components/shared/SingleActionLayout/SingleActionLayout';
import { SongThemeChip } from '@/components/shared/SongThemeChip';
import {
	EMOTION_TAGS,
	ENERGY_TAGS,
	GOSPEL_TAGS,
	SONG_TYPE_TAGS,
} from '@/config/constants';
import { TeamSong } from '@/graphql';
import { useAnalyticsEvent } from '@/providers/AnalyticsProvider';

import styles from './SongTags.module.scss';

type TagItem = {
	label: string;
	color?: ChipProps['color'];
	selected?: boolean;
};

type PartialTeamSong = Partial<
	Pick<TeamSong, 'energyTags' | 'emotionTags' | 'gospelTags' | 'songTypeTags'>
>;

export const SongTags: React.FC<{
	teamSong: PartialTeamSong;
	onSubmit: (values: PartialTeamSong) => void;
	onChange?: (tags: PartialTeamSong) => void;
	onMount?: () => void;
}> = ({ teamSong, onSubmit, onChange }) => {
	const [touched, setTouched] = useState(false);
	const [songTags, setSongTags] = useState(teamSong);
	const handleChange = (tags: PartialTeamSong) => {
		onChange && onChange(tags);

		setSongTags(tags);
		setTouched(true);
	};

	const { energyTags, emotionTags, gospelTags, songTypeTags } = songTags;

	const energyTagsMap: TagItem[] = ENERGY_TAGS.map((item) => ({
		...item,
		selected: energyTags?.includes(item.label),
	}));
	const emotionTagsMap: TagItem[] = (EMOTION_TAGS as TagItem[]).map(
		(item) => ({
			...item,
			selected: emotionTags?.includes(item.label),
		}),
	);
	const gospelTagsMap: TagItem[] = GOSPEL_TAGS.map((item) => ({
		...item,
		selected: gospelTags?.includes(item.label),
	}));
	const songTypeTagsMap: TagItem[] = SONG_TYPE_TAGS.map((item) => ({
		...item,
		selected: songTypeTags?.includes(item.label),
	}));

	return (
		<SingleActionLayout
			actionLabel="Save"
			actionDisabled={!touched}
			onActionClick={() => {
				onSubmit(songTags);
			}}
			contentClassName={styles['root']}
		>
			<div className={styles['title']}>
				Select all the themes that apply to this song
			</div>
			<div className={styles['subtitle']}>
				You can always skip this step and update at a later time.
			</div>
			<Label>Energy (Select One)</Label>
			<ChipList>
				<RenderChips
					onChange={(_i, label, value) => {
						// setTeamSong((state) => {
						const newState = !value;
						handleChange({
							emotionTags,
							gospelTags,
							energyTags: newState ? [label] : [],
							songTypeTags,
						});
						// });
					}}
					items={energyTagsMap}
				/>
			</ChipList>
			{/* <div className="vsb-20" />
			<Label>Emotional (Select One or many)</Label>
			<ChipList>
				<RenderChips
					onChange={(_i, label, value) => {
						// setTeamSong((state) => {
						const newState = !value;
						let tags = emotionTags || [];
						if (newState) {
							tags = [...tags, label];
						} else {
							tags = tags.filter((i) => i !== label);
						}
						onChange({
							emotionTags: tags,
							gospelTags,
							energyTags,
						});
						// });
					}}
					items={emotionTagsMap}
				/>
			</ChipList> */}
			<div className="vsb-20" />
			<Label>Gospel Story (Select One or many)</Label>
			<ChipList>
				<RenderChips
					onChange={(_i, label, value) => {
						// setTeamSong((state) => {
						const newState = !value;
						let tags = gospelTags || [];
						if (newState) {
							tags = [...tags, label];
						} else {
							tags = tags.filter((i) => i !== label);
						}
						handleChange({
							emotionTags,
							gospelTags: tags,
							energyTags,
							songTypeTags,
						});
						// });
					}}
					items={gospelTagsMap}
				/>
			</ChipList>
			<div className="vsb-20" />
			<Label>Song type (Select One)</Label>
			<ChipList>
				<RenderChips
					onChange={(_i, label, value) => {
						// setTeamSong((state) => {
						const newState = !value;
						handleChange({
							emotionTags,
							gospelTags,
							energyTags,
							songTypeTags: newState ? [label] : [],
						});
						// });
					}}
					items={songTypeTagsMap}
				/>
			</ChipList>
		</SingleActionLayout>
	);
};

const RenderChips: React.FC<{
	items: TagItem[];
	onChange?: (selectedIndex: number, label: string, value: boolean) => void;
}> = ({ items, onChange }) => {
	const noneSelected = Boolean(items.filter((i) => i.selected).length);
	return (
		<ChipList>
			{items.map((t, i) => (
				<SongThemeChip
					key={`${i}-${t}`}
					themeTag={t.label}
					disabled={noneSelected && !t.selected}
					onClick={() => {
						if (onChange) {
							onChange(i, t.label, !!t.selected);
						}
					}}
				/>
			))}
		</ChipList>
	);
};
